import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
  createContext
} from "react";
import { marked } from "marked";
import hljs from "highlight.js";
import "highlight.js/styles/github.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./globals-wave.css";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  ReferenceLine
} from "recharts";




function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for insecure contexts or old browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}


// API Base URL
//const API_BASE_URL = "http://localhost:8080"; //backend api url
//const API_BASE_URL = "http://49.50.78.106:8000"; //backend api url
//const VS_URL = "http://49.50.78.106:8088"; //weaviate vector search url
//const VS_URL1 = "http://49.50.78.106:8088"; //weaviate vector search url


const API_BASE_URL = "http://45.194.47.79:8000"; //backend api url
const VS_URL = "http://45.194.47.79:30808"; //weaviate vector search url
const VS_URL1 = "http://45.194.47.79:30808"; //weaviate vector search url




const safeJSON = (value) => {
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};
const isDateLike = (v) => {
  if (v == null) return false;
  if (/^\d{4}(-\d{2}){0,2}$/.test(String(v))) return true;
  const d = new Date(v);
  return !isNaN(d.getTime());
};
const toDate = (v) => (isDateLike(v) ? new Date(v) : null);
const rowTotal = (row, yKeys) =>
  (yKeys || []).reduce(
    (acc, key) => acc + (typeof row[key] === "number" ? row[key] : Number(row[key] || 0)),
    0
  );

const parseSSEPayloads = (chunk) => {
  const parts = chunk.split(/\r?\n/).filter(Boolean);
  const payloads = [];
  for (const line of parts) {
    if (line.startsWith("data:")) {
      payloads.push(line.slice(5).trim());
    }
  }
  return payloads.length ? payloads : null;
};

const chartStyles = {
  card: {
    border: "1px solid rgba(148,163,184,0.2)",
    borderRadius: 16,
    padding: 16,
    background: "var(--bg-tertiary, #fff)",
    boxShadow: "var(--shadow-sm, 0 8px 24px rgba(15,23,42,0.05))",
    marginBottom: 16
  },
  title: { fontWeight: 600, marginBottom: 6 },
  desc: { fontSize: 13, color: "var(--text-secondary,#6b7280)", marginBottom: 12 }
};

const chartColors = [
  "#2563eb",
  "#7c3aed",
  "#db2777",
  "#ea580c",
  "#16a34a",
  "#0d9488",
  "#0891b2",
  "#9333ea",
  "#f97316",
  "#0ea5e9"
];
const getSeriesColor = (index) => chartColors[index % chartColors.length];

const ensureArray = (value) => (Array.isArray(value) ? value : value != null ? [value] : []);
const normalizeMessage = (message) => ({
  chartSpec: null,
  chartSpecs: null,
  tables: [],
  agentEvents: [],
  ...message,
  tables: message?.tables ?? [],
  agentEvents: message?.agentEvents ?? []
});
const extractTextFromPayload = (payload) => {
  if (!payload || typeof payload !== "object") return null;
  const fields = ["content", "text", "answer", "message", "output"];
  for (const key of fields) {
    const val = payload[key];
    if (typeof val === "string" && val.trim()) return val;
  }
  return null;
};
const isAgentEventPayload = (payload) =>
  payload &&
  typeof payload === "object" &&
  typeof payload.graph_id === "string" &&
  typeof payload.type === "string";

function ChartRenderer({ spec }) {
  if (!spec) return null;
  const { type } = spec;
  if (["line", "bar", "area"].includes(type)) {
    return <RenderCartesian kind={type} spec={spec} />;
  }
  if (type === "pie") return <RenderPie spec={spec} />;
  if (type === "scatter") return <RenderScatter spec={spec} />;
  return <div style={{ color: "#c00" }}>Unsupported chart type: {String(type)}</div>;
}

function RenderCartesian({ kind, spec }) {
  const { xKey, yKeys = [], labels = {}, data = [], options = {} } = spec;
  const { stacked = false, smooth = false, grid = true } = options;

  return (
    <ResponsiveContainer width="100%" height={360}>
      {kind === "line" ? (
        <LineChart data={data} margin={{ top: 12, right: 12, bottom: 0, left: 0 }}>
          {grid && <CartesianGrid strokeDasharray="3 3" />}
          <XAxis dataKey={xKey} />
          <YAxis />
          <Tooltip />
          <Legend />
          {yKeys.map((k, idx) => {
            const color = getSeriesColor(idx);
            return (
              <Line
                key={k}
                type={smooth ? "monotone" : "linear"}
                dataKey={k}
                name={labels[k] || k}
                stroke={color}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
            );
          })}
        </LineChart>
      ) : kind === "bar" ? (
        <BarChart data={data} margin={{ top: 12, right: 12, bottom: 0, left: 0 }}>
          {grid && <CartesianGrid strokeDasharray="3 3" />}
          <XAxis dataKey={xKey} />
          <YAxis />
          <Tooltip />
          <Legend />
          {yKeys.map((k, idx) => (
            <Bar
              key={k}
              dataKey={k}
              name={labels[k] || k}
              stackId={stacked ? "a" : undefined}
              fill={getSeriesColor(idx)}
            />
          ))}
        </BarChart>
      ) : (
        <AreaChart data={data} margin={{ top: 12, right: 12, bottom: 0, left: 0 }}>
          {grid && <CartesianGrid strokeDasharray="3 3" />}
          <XAxis dataKey={xKey} />
          <YAxis />
          <Tooltip />
          <Legend />
          {yKeys.map((k, idx) => {
            const color = getSeriesColor(idx);
            return (
              <Area
                key={k}
                type={smooth ? "monotone" : "linear"}
                dataKey={k}
                name={labels[k] || k}
                stroke={color}
                fill={color}
                fillOpacity={0.25}
                isAnimationActive={false}
              />
            );
          })}
        </AreaChart>
      )}
    </ResponsiveContainer>
  );
}

function RenderPie({ spec }) {
  const { xKey, yKeys = [], data = [], options = {} } = spec;
  const valueKey = yKeys[0];
  const { innerRadius = 40, outerRadius = 80 } = options;

  return (
    <ResponsiveContainer width="100%" height={360}>
      <PieChart>
        <Tooltip />
        <Legend />
        <Pie
          data={data}
          dataKey={valueKey}
          nameKey={xKey}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          isAnimationActive={false}
        >
          {data.map((_, idx) => {
            const color = getSeriesColor(idx);
            return <Cell key={`slice-${idx}`} fill={color} stroke={color} />;
          })}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
}

function RenderScatter({ spec }) {
  const { series = [], xKey = "x", yKeys = ["y"] } = spec;
  const yLabel = yKeys[0] || "y";
  return (
    <ResponsiveContainer width="100%" height={360}>
      <ScatterChart margin={{ top: 12, right: 12, bottom: 0, left: 0 }}>
        <CartesianGrid />
        <XAxis type="number" dataKey={xKey} name={xKey} />
        <YAxis type="number" dataKey={yLabel} name={yLabel} />
        <Tooltip cursor={{ strokeDasharray: "3 3" }} />
        <Legend />
        <ReferenceLine y={0} stroke="#999" />
        {series.map((s, idx) => (
          <Scatter
            key={idx}
            name={s.name || `Series ${idx + 1}`}
            data={s.data || []}
            fill={getSeriesColor(idx)}
            line
          />
        ))}
      </ScatterChart>
    </ResponsiveContainer>
  );
}

const tableStyles = {
  wrapper: { overflowX: "auto" },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    lineHeight: 1.5
  },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: "1px solid rgba(148,163,184,0.4)",
    background: "rgba(148,163,184,0.15)",
    fontWeight: 600,
    whiteSpace: "nowrap"
  },
  td: {
    padding: "8px 10px",
    borderBottom: "1px solid rgba(148,163,184,0.2)",
    verticalAlign: "top",
    whiteSpace: "nowrap"
  }
};

const formatTableValue = (value) => {
  if (value == null) return "";
  if (typeof value === "number") return value.toLocaleString();
  if (typeof value === "boolean") return value ? "true" : "false";
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "string" && /^https?:\/\//i.test(value)) {
    return (
      <a href={value} target="_blank" rel="noreferrer">
        {value}
      </a>
    );
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const deriveColumnsFromRows = (rows, seed = []) => {
  if (!Array.isArray(rows) || !rows.length) return [];
  const columns = Array.isArray(seed) ? [...seed] : [];
  rows.forEach((row) => {
    Object.keys(row || {}).forEach((key) => {
      if (!columns.includes(key)) columns.push(key);
    });
  });
  return columns;
};

const MAX_TABLE_ROWS = 200;
const stringifyAgentEvent = (event) => {
  if (!event) return "";
  if (typeof event === "string") return event;
  try {
    return JSON.stringify(event);
  } catch {
    return String(event);
  }
};

function DataTable({ table }) {
  if (!table) return null;
  const { columns = [], rows = [], meta } = table;
  if (!columns.length) return null;

  const displayRows = rows.slice(0, MAX_TABLE_ROWS);
  const truncated = rows.length > MAX_TABLE_ROWS;

  return (
    <div>
      {(meta?.count != null || truncated) && (
        <div style={{ fontSize: 12, color: "var(--text-secondary,#6b7280)", marginBottom: 8 }}>
          {meta?.count != null ? `${meta.count} rows` : `${rows.length} rows`}
          {` · ${columns.length} columns`}
          {meta?.elapsed_ms != null && ` · ${meta.elapsed_ms}ms`}
          {truncated && ` · showing first ${MAX_TABLE_ROWS}`}
        </div>
      )}
      <div style={tableStyles.wrapper}>
        <table style={tableStyles.table}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col} style={tableStyles.th}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, idx) => (
              <tr key={idx}>
                {columns.map((col) => (
                  <td key={col} style={tableStyles.td}>
                    {formatTableValue(row?.[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FiltersBar({ spec, filters, onChange }) {
  const { type, xKey, yKeys = [], data = [], labels = {} } = spec;
  const [search, setSearch] = useState(filters.search);
  const [topN, setTopN] = useState(filters.topN);
  const [dateMin, setDateMin] = useState(filters.dateMin || "");
  const [dateMax, setDateMax] = useState(filters.dateMax || "");

  const xValues = useMemo(() => data.map((d) => d?.[xKey]).filter((v) => v != null), [data, xKey]);
  const xDates = useMemo(() => {
    const ds = xValues.map(toDate).filter(Boolean);
    if (!ds.length) return null;
    return {
      min: new Date(Math.min(...ds.map((d) => d.getTime()))),
      max: new Date(Math.max(...ds.map((d) => d.getTime())))
    };
  }, [xValues]);

  const toggleSeries = (key) => {
    const next = new Set(filters.activeSeries);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange({ activeSeries: next });
  };

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
      {type !== "pie" && yKeys.length > 0 && (
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600 }}>Series:</span>
          {yKeys.map((key) => {
            const active = filters.activeSeries.has(key);
            return (
              <label
                key={key}
                style={{
                  padding: "6px 10px",
                  borderRadius: 999,
                  border: `1px solid ${active ? "#2563eb" : "#d1d5db"}`,
                  background: active ? "#2563eb" : "#fff",
                  color: active ? "#fff" : "#111827",
                  cursor: "pointer",
                  userSelect: "none"
                }}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleSeries(key)}
                  style={{ display: "none" }}
                />
                {labels?.[key] || key}
              </label>
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontWeight: 600 }}>Filter:</span>
        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            onChange({ search: e.target.value });
          }}
          placeholder={`Search ${xKey}…`}
          style={{ minWidth: 160 }}
          className="input"
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontWeight: 600 }}>Top N:</span>
        <input
          type="number"
          min="1"
          value={topN}
          onChange={(e) => {
            setTopN(e.target.value);
            onChange({ topN: e.target.value });
          }}
          style={{ width: 90 }}
          className="input"
        />
      </div>

      {xDates && (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontWeight: 600 }}>Date:</span>
          <input
            type="date"
            value={dateMin}
            onChange={(e) => {
              setDateMin(e.target.value);
              onChange({ dateMin: e.target.value });
            }}
            className="input"
          />
          <span>to</span>
          <input
            type="date"
            value={dateMax}
            onChange={(e) => {
              setDateMax(e.target.value);
              onChange({ dateMax: e.target.value });
            }}
            className="input"
          />
          <span style={{ opacity: 0.6, fontSize: 12 }}>
            {xDates.min.toISOString().slice(0, 10)} → {xDates.max.toISOString().slice(0, 10)}
          </span>
        </div>
      )}
    </div>
  );
}

function InteractiveChart({ spec }) {
  const [filters, setFilters] = useState({
    activeSeries: new Set(spec.yKeys || []),
    search: "",
    topN: "",
    dateMin: "",
    dateMax: ""
  });

  const filteredSpec = useMemo(() => {
    if (spec.type === "scatter") {
      const filteredSeries = ensureArray(spec.series).map((series) => ({
        ...series,
        data: ensureArray(series.data).filter(
          (point) => filters.activeSeries.size === 0 || filters.activeSeries.has(series.name)
        )
      }));
      return { ...spec, series: filteredSeries };
    }

    let yKeys = ensureArray(spec.yKeys);
    let data = ensureArray(spec.data);
    const { type, xKey } = spec;

    if (filters.activeSeries.size) {
      yKeys = yKeys.filter((key) => filters.activeSeries.has(key));
    }

    if (filters.search) {
      const q = filters.search.toLowerCase();
      data = data.filter((row) => String(row?.[xKey] ?? "").toLowerCase().includes(q));
    }

    if (filters.dateMin || filters.dateMax) {
      data = data.filter((row) => {
        const d = toDate(row?.[xKey]);
        if (!d) return false;
        const minPass = filters.dateMin ? d >= new Date(filters.dateMin) : true;
        const maxPass = filters.dateMax ? d <= new Date(filters.dateMax) : true;
        return minPass && maxPass;
      });
    }

    if (type !== "pie" && Number(filters.topN) > 0) {
      const n = Number(filters.topN);
      data = [...data]
        .sort((a, b) => rowTotal(b, yKeys) - rowTotal(a, yKeys))
        .slice(0, n);
    }

    if (type === "pie" && Number(filters.topN) > 0) {
      const yKey = yKeys[0];
      const n = Number(filters.topN);
      data = [...data]
        .sort((a, b) => (Number(b?.[yKey]) || 0) - (Number(a?.[yKey]) || 0))
        .slice(0, n);
    }

    return { ...spec, yKeys, data };
  }, [spec, filters]);

  return (
    <div>
      <FiltersBar
        spec={spec}
        filters={filters}
        onChange={(update) =>
          setFilters((prev) => ({
            activeSeries: update.activeSeries ?? prev.activeSeries,
            search: update.search ?? prev.search,
            topN: update.topN ?? prev.topN,
            dateMin: update.dateMin ?? prev.dateMin,
            dateMax: update.dateMax ?? prev.dateMax
          }))
        }
      />
      <ChartRenderer spec={filteredSpec} />
    </div>
  );
}

function mapMultiChartPayloadToSpecs(payload) {
  let charts = null;
  if (payload?.chart?.charts && Array.isArray(payload.chart.charts)) charts = payload.chart.charts;
  else if (Array.isArray(payload?.charts)) charts = payload.charts;
  else if (Array.isArray(payload)) charts = payload;

  if (!charts) {
    const single = mapUserChartToChartSpec(payload?.chart || payload);
    return single ? [single] : null;
  }

  const specs = charts.map((c) => mapUserChartToChartSpec(c)).filter(Boolean);
  return specs.length ? specs : null;
}

function mapUserChartToChartSpec(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (payload.chart && typeof payload.chart === "object") return mapUserChartToChartSpec(payload.chart);

  const t = String(payload.chart_type || payload.type || "").toLowerCase();
  let type = "bar";
  if (t.includes("line")) type = "line";
  else if (t.includes("area")) type = "area";
  else if (t.includes("pie") || t.includes("donut")) type = "pie";
  else if (t.includes("scatter") || t.includes("bubble")) type = "scatter";

  const title = payload.title || payload.name || payload.label || "";
  const description = payload.description || payload.caption || payload.summary || "";
  const options = payload.options || {};
  const stacked = !!payload.stacked || options.stacked || false;

  if (Array.isArray(payload.series) && type === "scatter") {
    const series = payload.series.map((s, idx) => ({
      name: s?.name ?? `Series ${idx + 1}`,
      data: ensureArray(s?.data).map((point) => ({
        x: Number(point?.x ?? point?.X ?? 0),
        y: Number(point?.y ?? point?.Y ?? 0)
      }))
    }));
    return { type: "scatter", title, description, series, xKey: "x", yKeys: ["y"] };
  }

  if (Array.isArray(payload.chart_data) && payload.chart_data.length) {
    const first = payload.chart_data[0] || {};
    if (type === "pie" || ("value" in first && ("label" in first || "name" in first || "x" in first))) {
      const rows = payload.chart_data.map((row) => ({
        name: row.label ?? row.name ?? row.category ?? row.x ?? row.id ?? "",
        value: Number(row.value ?? row.y ?? row.amount ?? 0)
      }));
      return {
        type: "pie",
        title,
        description,
        data: rows,
        xKey: "name",
        yKeys: ["value"],
        options
      };
    }
    if (type === "scatter" && first.data) {
      const series = payload.chart_data.map((s, idx) => ({
        name: s?.name ?? s?.label ?? `Series ${idx + 1}`,
        data: ensureArray(s?.data).map((point) => ({
          x: Number(point?.x ?? point?.X ?? 0),
          y: Number(point?.y ?? point?.Y ?? 0)
        }))
      }));
      return { type: "scatter", title, description, series, xKey: "x", yKeys: ["y"] };
    }
  }

  if (Array.isArray(payload.data)) {
    if (type === "pie") {
      const rows = payload.data.map((row) => ({
        name: row.name ?? row.label ?? row.x ?? row.category ?? row.Category ?? "",
        value: Number(row.value ?? row.y ?? row.amount ?? 0)
      }));
      return {
        type: "pie",
        title,
        description,
        data: rows,
        xKey: "name",
        yKeys: ["value"],
        options
      };
    }

    const xKey = payload.xKey || payload.x_field || payload.x_axis || "x";
    const yKeys = ensureArray(payload.yKeys || payload.y_fields || payload.metrics);
    if (!yKeys.length && payload.data.length) {
      const numericKeys = Object.keys(payload.data[0] || {}).filter(
        (key) => typeof payload.data[0][key] === "number"
      );
      yKeys.push(...numericKeys.filter((k) => k !== xKey));
    }
    if (!yKeys.length) return null;

    const labels = { ...(payload.labels || {}) };
    yKeys.forEach((key) => {
      if (!labels[key]) labels[key] = key;
    });

    return {
      type,
      title,
      description,
      xKey,
      yKeys,
      labels,
      data: payload.data,
      options: { ...options, stacked }
    };
  }

  const categories = payload.chart_data?.categories || payload.categories;
  const seriesArr = payload.chart_data?.series || payload.series;
  if (Array.isArray(categories) && Array.isArray(seriesArr)) {
    const xKey = "category";
    const labels = {};
    const yKeys = [];
    const rows = categories.map((c) => ({ [xKey]: c }));
    seriesArr.forEach((series, idx) => {
      const name = series?.name ?? `Series ${idx + 1}`;
      labels[name] = name;
      yKeys.push(name);
      const data = Array.isArray(series?.data) ? series.data : [];
      for (let i = 0; i < categories.length; i += 1) {
        rows[i][name] = Number(data[i] ?? 0) || 0;
      }
    });
    return {
      type,
      title,
      description,
      xKey,
      yKeys,
      labels,
      data: rows,
      options: { ...options, stacked }
    };
  }

  const xVals = payload.chart_data?.xValues || payload.xValues;
  const yVals = payload.chart_data?.yValues || payload.yValues;
  if (Array.isArray(xVals) && Array.isArray(yVals)) {
    const xKey = "x";
    const seriesName = String(
      payload.series_name || payload.series || payload.y_axis_title || "value"
    );
    const rows = xVals.map((x, idx) => ({
      [xKey]: x,
      [seriesName]: Number(yVals[idx] ?? 0) || 0
    }));
    return {
      type,
      title,
      description,
      xKey,
      yKeys: [seriesName],
      labels: { [seriesName]: seriesName },
      data: rows,
      options: { ...options, stacked: false }
    };
  }

  return null;
}

function mapPayloadToTableSpec(payload) {
  if (!payload) return null;

  if (Array.isArray(payload)) {
    const rows = payload.filter(
      (row) => row && typeof row === "object" && !Array.isArray(row)
    );
    if (!rows.length) return null;
    const columns = deriveColumnsFromRows(rows);
    if (!columns.length) return null;
    return {
      title: "",
      description: "",
      columns,
      rows,
      meta: null
    };
  }

  if (typeof payload !== "object") return null;

  const dataBlock =
    payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)
      ? payload.data
      : null;

  let rows = null;
  if (Array.isArray(payload.rows)) rows = payload.rows;
  else if (Array.isArray(payload.records)) rows = payload.records;
  else if (Array.isArray(payload.table)) rows = payload.table;
  else if (Array.isArray(payload.data) && payload.data.every((row) => row && typeof row === "object")) {
    rows = payload.data;
  } else if (Array.isArray(dataBlock?.rows)) rows = dataBlock.rows;
  else if (Array.isArray(dataBlock?.records)) rows = dataBlock.records;

  if (!rows || !rows.length || !rows.every((row) => row && typeof row === "object")) return null;

  let columns =
    (Array.isArray(payload.columns) && payload.columns.length && payload.columns) ||
    (Array.isArray(dataBlock?.columns) && dataBlock.columns) ||
    (Array.isArray(payload.meta?.columns) && payload.meta.columns) ||
    (Array.isArray(dataBlock?.meta?.columns) && dataBlock.meta.columns) ||
    null;

  if (!columns) columns = deriveColumnsFromRows(rows);

  if (!columns.length) return null;

  const meta = payload.meta || dataBlock?.meta || null;
  const title =
    payload.title ||
    payload.name ||
    payload.label ||
    dataBlock?.title ||
    meta?.title ||
    "";
  const description =
    payload.description ||
    payload.summary ||
    payload.caption ||
    dataBlock?.description ||
    meta?.description ||
    "";

  return {
    title,
    description,
    columns,
    rows,
    meta
  };
}

function isChartSpec(spec) {
  if (!spec || typeof spec !== "object") return false;
  const { type } = spec;
  if (!type) return false;
  if (["line", "bar", "area"].includes(type)) {
    return Array.isArray(spec.data) && spec.xKey && Array.isArray(spec.yKeys) && spec.yKeys.length;
  }
  if (type === "pie") {
    return (
      Array.isArray(spec.data) &&
      spec.xKey === "name" &&
      Array.isArray(spec.yKeys) &&
      spec.yKeys[0] === "value"
    );
  }
  if (type === "scatter") {
    return Array.isArray(spec.series);
  }
  return false;
}

//@Aj 13th Spet 2025  Added backend settings save and load
const backend = {
  settings: {
    mode: "edit",
    newName: "",
    async save(params) {    // <-- not an arrow
      if (this.mode === "add") {
        const name = this.newName.trim();
        if (!name) return;
        if (!/^[A-Z]/.test(name)) {
          alert("Index name must start with a capital letter");
          return;
        }
      }

      params["mode"] = this.mode;
      params["newName"] = this.newName;

      const response = await fetch(`${API_BASE_URL}/savesettings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });

      if (response.ok) {
        if (this.mode === "add") {
          // make sure these exist in settings
          this.setNewIndexName?.("");
          await this.fetchIndexes?.();
          this.setSelectedIndex?.(this.newName);
        }
      } else {
        const errorText = await response.text();
        console.error("Failed to create index:", errorText);
        alert("Failed to create index: " + errorText);
      }
    },

    async load(params) {
      const resp = await fetch(
        `${API_BASE_URL}/loadsettings?indexId=${params.indexId}`
      );
      return resp.json();
    },
  },
};

// const backend = {
//   settings: {
//     mode: "edit",
//     newName: "",
//     save: async (params) => {
//       if (this.mode === "add") {
//         const name = this.newName.trim();
//         if (!name) return;
//         if (!/^[A-Z]/.test(name)) {
//           alert("Index name must start with a capital letter");
//           return;
//         }
//       }
//       params["mode"] = this.mode ;
//       params["newName"] = this.newName;
//       const response =  await fetch(`${API_BASE_URL}/savesettings`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(params),
//       });
//       if (response.ok) {
//         if (this.mode === "add") {
//           this.setNewIndexName("");
//           await this.fetchIndexes();
//           this.setSelectedIndex(this.newName);
//         }
//       } else {
//         const errorText = await response.text();
//         console.error("Failed to create index:", errorText);
//         alert("Failed to create index: " + errorText);
//       }

//     },
//     load: async (params) => {
//       const resp = await fetch(`${API_BASE_URL}/loadsettings?indexId=${params.indexId}`);
//       return resp.json();
//     }
//   }
// }

hljs.configure({ ignoreUnescapedHTML: true });
marked.setOptions({
  highlight(code, lang) {
    if (lang && hljs.getLanguage(lang)) {
      return hljs.highlight(code, { language: lang }).value;
    }
    return hljs.highlightAuto(code).value;
  },
  breaks: true,
});

const ThemeContext = createContext({
  theme: 'light',
  toggleTheme: () => {},
});

function useTheme() {
  return useContext(ThemeContext);
}

const toBool = (v) => v === true;

const EMBEDDING_MODELS = {
  'text-embedding-3-small': { dimensions: 1536, description: 'Cost-effective, good quality' },
  'text-embedding-3-large': { dimensions: 3072, description: 'Highest quality, more expensive' },
  'bge-small-en-v1.5': { dimensions: 384, description: 'Lightweight, fast inference' },
  'bge-large-en-v1.5': { dimensions: 1024, description: 'Strong performance, OSS' },
  'nomic-embed-text-v1': { dimensions: 768, description: 'Good general purpose, OSS' },
  'gte-large': { dimensions: 1024, description: 'Strong multilingual support' },
  'custom': { dimensions: 1536, description: 'Custom model configuration' },
};

const MEMORY_PROFILES = {
  'small': { vectorCache: 100000, hnswM: 32, efConstruction: 128 },
  'medium': { vectorCache: 1000000, hnswM: 64, efConstruction: 200 },
  'large': { vectorCache: 5000000, hnswM: 96, efConstruction: 300 },
};

function getWeaviateDefaults() {
  return {
    vectorIndexType: 'flat',
    hnswMaxConnections: 64,
    hnswEfConstruction: 200,
    hnswEf: 64,
    vectorCacheMaxObjects: 1000000,
    enableInvertedIndex: true,
    enableBM25: true,
    bm25K1: 1.2,
    bm25B: 0.75,
    multiTenancy: false,
    tenant: 'default',
    shardingConfig: {
      virtualPerPhysical: 128,
      desiredCount: 1,
      actualCount: 1,
    },
    distanceMetric: 'cosine',
    useExternalEmbeddings: true,
    keywordAnalyzer: {
      language: 'en',
      stopwords: true,
      lowercase: true,
      stemming: true,
    },
    memoryProfile: 'medium',
  };
}

function getKdbAiDefaults() {
  return {
    indexType: 'flat',
    metricType: 'cosine',
    nlist: 1024,
    nprobe: 10,
    hnswM: 16,
    hnswEfConstruction: 200,
    hnswEfSearch: 64,
    enableGpu: false,
    compressionType: 'none',
    pqM: 8,
    partitionColumns: [],
    normalizeVectors: true,
    maxBatchSearch: 1000,
    timeWindows: { enabled: false, defaultWindow: '30d' },
  };
}

function getRecommendedVectorizationSettings(dbType) {
  return {
    vectorDatabase: dbType,
    model: 'text-embedding-3-small',
    dimensions: 1536,
    chunkSize: 800,
    chunkOverlap: 160,
    separators: ['\n\n', '\n', '.', '?', '!', ';', ',', ' '],
    customSeparators: '',
    backupSeparators: true,
    textCleaning: {
      removePatterns: [
        "\\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Z|a-z]{2,}\\b", // Emails
        "https?://\\S+", // URLs
        "\\[\\d+\\]", // Citations like [1], [2], etc.
        "^\\s*Page \\d+\\s*$", // Page numbers
      ],
      removeMultipleBlankLines: true,
      normalizeWhitespace: true,
      stripNumbers: false,
      removeFooterLines: true,
      removeSpecialChars: false,
      lowercaseNormalization: false,
    },
    titleInjection: true,
    metadataStorage: true,
    weaviateSettings: getWeaviateDefaults(),
    kdbAiSettings: getKdbAiDefaults(),
  };
}

function getRecommendedRetrievalSettings() {
  return {
    retrievalMode: 'vector',
    fusionStrategy: 'rrf',
    alpha: 0.4,
    poolK: 50,
    finalChunks: 8,
    reranking: true,
    rerankingModel: 'cross-encoder/ms-marco-MiniLM-L-6-v2',
    similarityThreshold: 0.7,
    maxTokensPerChunk: 512,
    mmr: { enabled: true, lambda: 0.3, finalK: 8 },
    weighting: { titleBoost: 1.2, headingBoost: 1.1, filenameBoost: 1.0 },
    groupByDocument: false,
    maxChunksPerDoc: 3,
    mergeAdjacentChunks: false,
  };
}

function getDefaultRagSettings() {
  return {
    vectorization: getRecommendedVectorizationSettings('weaviate'),
    search: {
      query: '',
      maxResults: 10,
      tenant: 'default',
      timeFilter: { enabled: false, range: 'last-30d' },
      metadataFilters: {},
      retrieval: getRecommendedRetrievalSettings(),
    }
  };
}

function getAutoAdjustedPqM(dimensions, currentPqM) {
  const factors = [];
  for (let i = 1; i <= Math.min(64, dimensions); i++) {
    if (dimensions % i === 0) {
      factors.push(i);
    }
  }
  
  return factors.reduce((prev, curr) => 
    Math.abs(curr - currentPqM) < Math.abs(prev - currentPqM) ? curr : prev
  );
}

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState('light');

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

function VectorizationSettingsPage({ ragSettings, setRagSettings, indexId }) {
  const [mode, setMode] = useState('vectorization');
  const [filePath, setFilePath] = useState('');
  const [className, setClassName] = useState('');
  const [newPattern, setNewPattern] = useState('');
  const [newPartitionColumn, setNewPartitionColumn] = useState('');
  const [testText, setTestText] = useState('');
  const [cleanedText, setCleanedText] = useState('');
  const [customModel, setCustomModel] = useState({ name: '', dimensions: 1536 });
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isLoadDialogOpen, setIsLoadDialogOpen] = useState(false);
  const [jsonToLoad, setJsonToLoad] = useState('');

  const settings = ragSettings.vectorization;

  const isSearchMode = mode === 'search';

  const pqError = settings.kdbAiSettings.compressionType === 'pq' && 
                  settings.dimensions % settings.kdbAiSettings.pqM !== 0;
  const needsNormalization = (settings.vectorDatabase === 'weaviate' && settings.weaviateSettings.distanceMetric === 'cosine') ||
                            (settings.vectorDatabase === 'kdb-ai' && settings.kdbAiSettings.metricType === 'cosine');

  useEffect(() => {
    if (settings.vectorDatabase === 'weaviate' && settings.weaviateSettings) {
      const profile = MEMORY_PROFILES[settings.weaviateSettings.memoryProfile];
      if (profile) {
        updateVectorizationSettings('weaviateSettings.vectorCacheMaxObjects', profile.vectorCache);
        updateVectorizationSettings('weaviateSettings.hnswMaxConnections', profile.hnswM);
        updateVectorizationSettings('weaviateSettings.hnswEfConstruction', profile.efConstruction);
      }
    }
  }, [settings.vectorDatabase, settings.weaviateSettings]);

  useEffect(() => {
    if (pqError && settings.kdbAiSettings.compressionType === 'pq') {
      const adjustedPqM = getAutoAdjustedPqM(settings.dimensions, settings.kdbAiSettings.pqM);
      updateVectorizationSettings('kdbAiSettings.pqM', adjustedPqM);
    }
  }, [settings.dimensions, settings.kdbAiSettings.compressionType]);

  useEffect(() => {
    const currentPercentage = (settings.chunkOverlap / settings.chunkSize) * 100;
    if (currentPercentage >= 10 && currentPercentage <= 30) {
      const newOverlap = Math.round((currentPercentage / 100) * settings.chunkSize);
      if (newOverlap !== settings.chunkOverlap) {
        updateVectorizationSettings('chunkOverlap', newOverlap);
      }
    }
  }, [settings.chunkSize]);

  const updateVectorizationSettings = (path, value) => {
    setRagSettings(prev => {
      const keys = path.split('.');
      const updated = JSON.parse(JSON.stringify(prev)); // Deep copy
      let current = updated.vectorization;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const addPattern = () => {
    if (newPattern.trim()) {
      updateVectorizationSettings('textCleaning.removePatterns', [...settings.textCleaning.removePatterns, newPattern.trim()]);
      setNewPattern('');
    }
  };

  const removePattern = (index) => {
    const patterns = settings.textCleaning.removePatterns.filter((_, i) => i !== index);
    updateVectorizationSettings('textCleaning.removePatterns', patterns);
  };

  const addPartitionColumn = () => {
    if (newPartitionColumn.trim() && !settings.kdbAiSettings.partitionColumns.includes(newPartitionColumn.trim())) {
      updateVectorizationSettings('kdbAiSettings.partitionColumns', [...settings.kdbAiSettings.partitionColumns, newPartitionColumn.trim()]);
      setNewPartitionColumn('');
    }
  };

  const removePartitionColumn = (index) => {
    const columns = settings.kdbAiSettings.partitionColumns.filter((_, i) => i !== index);
    updateVectorizationSettings('kdbAiSettings.partitionColumns', columns);
  };

  const applyRecommendedSettings = () => {
    const recommended = getRecommendedVectorizationSettings(settings.vectorDatabase);
    setRagSettings(prev => ({
      ...prev,
      vectorization: {
        ...prev.vectorization,
        ...recommended,
        textCleaning: {
          ...prev.vectorization.textCleaning,
          removeFooterLines: true,
          normalizeWhitespace: true,
          removeMultipleBlankLines: true,
        },
      }
    }));
  };

  const handleDatabaseChange = (dbType) => {
    const recommended = getRecommendedVectorizationSettings(dbType);
    setRagSettings(prev => ({
      ...prev,
      vectorization: {
        ...prev.vectorization,
        ...recommended,
      }
    }));
  };

  const testCleaning = () => {
    let cleaned = testText;
    cleaned = cleaned.replace(/\r\n/g, '\n');
    if (settings.textCleaning.normalizeWhitespace) {
      cleaned = cleaned.replace(/[ \t]+/g, ' ');
    }
    if (settings.textCleaning.removeMultipleBlankLines) {
      cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
    }
    settings.textCleaning.removePatterns.forEach(pattern => {
      try {
        const regex = new RegExp(pattern, 'g');
        cleaned = cleaned.replace(regex, '');
      } catch (e) {}
    });
    setCleanedText(cleaned);
  };

  const addCustomModel = () => {
    if (customModel.name.trim() && customModel.dimensions > 0) {
      const newModel = `custom-${customModel.name}`;
      EMBEDDING_MODELS[newModel] = {
        dimensions: customModel.dimensions,
        description: 'Custom model'
      };
      updateVectorizationSettings('model', newModel);
      updateVectorizationSettings('dimensions', customModel.dimensions);
      setCustomModel({ name: '', dimensions: 1536 });
    }
  };

  const handleLoadSettings = () => {
    if (!jsonToLoad.trim()) {
      alert("Error: Textarea is empty.");
      return;
    }
    try {
      const parsedSettings = JSON.parse(jsonToLoad);
      if (parsedSettings && typeof parsedSettings === 'object' && parsedSettings.vectorization && parsedSettings.search) {
        setRagSettings(parsedSettings);
        alert("Success: Settings imported.");
        setIsLoadDialogOpen(false);
        setJsonToLoad('');
      } else {
        alert("Invalid Format: The provided JSON does not appear to be a valid settings object.");
      }
    } catch (error) {
      alert("Parsing Error: Failed to parse JSON. Please check the format.");
    }
  };

  const handleSave = async () => {
    if (!indexId) {
      alert("Cannot save settings without an index name.");
      return;
    }
    try {
      //@Aj 13th Sept 2025  Added backend settings save and load
      //backend.settings.mode = "edit"; // or "add" based on your requirement
      await backend.settings.save({ indexId, settings: ragSettings });
      alert("Settings saved successfully!");
    } catch (error) {
      console.error("Failed to save settings:", error);
      alert("Failed to save settings.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <i className="fas fa-cogs text-primary text-2xl"></i>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Vectorization Settings</h1>
            <p className="text-muted-foreground">Configure embedding models, chunking, and database indexing</p>
          </div>
        </div>
        <button onClick={applyRecommendedSettings} disabled={isSearchMode} className="btn btn-outline">
          <i className="fas fa-brain mr-2"></i>
          Apply Recommended
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Operation Mode & Source</h3>
          <p className="card-description">
            Select 'Vectorization' to configure and run a new indexing job. Select 'Search' to view the configuration of an existing index (read-only).
          </p>
        </div>
        <div className="card-content space-y-4">
          <div className="flex items-center space-x-2">
            <label htmlFor="mode-switch" className={`text-sm font-medium leading-none ${mode === 'vectorization' ? 'font-bold' : ''}`}>Vectorization</label>
            <button
              role="switch"
              aria-checked={mode === 'search'}
              onClick={() => setMode(prev => prev === 'search' ? 'vectorization' : 'search')}
              className={`switch ${mode === 'search' ? 'switch-on' : ''}`}
            >
              <span className="switch-thumb" />
            </button>
            <label htmlFor="mode-switch" className={`text-sm font-medium leading-none ${mode === 'search' ? 'font-bold' : ''}`}>Search (Read-Only)</label>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="filePath" className="label">Path to File/Folder</label>
              <input
                id="filePath"
                value={filePath}
                onChange={(e) => setFilePath(e.target.value)}
                placeholder="/path/to/your/documents"
                disabled={isSearchMode}
                className="input"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="className" className="label">Class Name</label>
              <input
                id="className"
                value={className}
                onChange={(e) => setClassName(e.target.value)}
                placeholder="MyDocumentCollection"
                disabled={isSearchMode}
                className="input"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center gap-2"><i className="fas fa-database"></i>Vector Database</h3>
        </div>
        <div className="card-content space-y-4">
          <div className="space-y-2">
            <label htmlFor="vectorDatabase" className="label">Vector Database</label>
            <select value={settings.vectorDatabase} onChange={(e) => handleDatabaseChange(e.target.value)} disabled={isSearchMode} className="select">
              <option value="weaviate">Weaviate</option>
              <option value="kdb-ai">KDB.AI</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center gap-2"><i className="fas fa-bolt"></i>Embedding Model</h3>
        </div>
        <div className="card-content space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="model" className="label">Model</label>
              <select value={settings.model} onChange={(e) => {
                updateVectorizationSettings('model', e.target.value);
                const modelInfo = EMBEDDING_MODELS[e.target.value];
                if (modelInfo && e.target.value !== 'custom') {
                  updateVectorizationSettings('dimensions', modelInfo.dimensions);
                }
              }} disabled={isSearchMode} className="select">
                {Object.entries(EMBEDDING_MODELS).map(([key, info]) => (
                  <option key={key} value={key}>{key} ({info.dimensions}d) - {info.description}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label htmlFor="dimensions" className="label">Dimensions</label>
              <input id="dimensions" type="number" value={settings.dimensions} onChange={(e) => updateVectorizationSettings('dimensions', parseInt(e.target.value))} disabled={isSearchMode || settings.model !== 'custom'} className="input" />
              <p className="text-sm text-muted-foreground">{settings.model === 'custom' ? 'Custom dimensions' : 'Auto-detected from model'}</p>
            </div>
          </div>
          {settings.model === 'custom' && (
            <div className="border rounded-lg p-4 space-y-4">
              <label className="label">Add Custom Model</label>
              <div className="grid grid-cols-2 gap-4">
                <input placeholder="Model name" value={customModel.name} onChange={(e) => setCustomModel(prev => ({ ...prev, name: e.target.value }))} disabled={isSearchMode} className="input" />
                <input type="number" placeholder="Dimensions" value={customModel.dimensions} onChange={(e) => setCustomModel(prev => ({ ...prev, dimensions: parseInt(e.target.value) }))} disabled={isSearchMode} className="input" />
              </div>
              <button onClick={addCustomModel} disabled={isSearchMode} className="btn btn-sm">Add Model</button>
            </div>
          )}
        </div>
      </div>

      {settings.vectorDatabase === 'weaviate' && <WeaviateSettingsCard settings={settings.weaviateSettings} dimensions={settings.dimensions} updateSettings={updateVectorizationSettings} disabled={isSearchMode} />}
      {settings.vectorDatabase === 'kdb-ai' && <KdbAiSettingsCard settings={settings.kdbAiSettings} dimensions={settings.dimensions} updateSettings={updateVectorizationSettings} addPartitionColumn={addPartitionColumn} removePartitionColumn={removePartitionColumn} newPartitionColumn={newPartitionColumn} setNewPartitionColumn={setNewPartitionColumn} pqError={pqError} needsNormalization={needsNormalization} disabled={isSearchMode} />}

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Text Splitting & Processing</h3>
        </div>
        <div className="card-content space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="chunkSize" className="label">Target Chunk Length (≈ tokens)</label>
              <input id="chunkSize" type="number" value={settings.chunkSize} onChange={(e) => updateVectorizationSettings('chunkSize', parseInt(e.target.value))} min="100" max="2000" disabled={isSearchMode} className="input" />
              <p className="text-sm text-muted-foreground">Characters: ~{settings.chunkSize * 4}, Current: {settings.chunkSize} target tokens</p>
            </div>
            <div className="space-y-2">
              <label htmlFor="chunkOverlap" className="label">Chunk Overlap (%)</label>
              <input id="chunkOverlap" type="number" value={Math.round((settings.chunkOverlap / settings.chunkSize) * 100)} onChange={(e) => {
                const percentage = parseInt(e.target.value);
                updateVectorizationSettings('chunkOverlap', Math.round((percentage / 100) * settings.chunkSize));
              }} min="10" max="30" disabled={isSearchMode} className="input" />
              <p className="text-sm text-muted-foreground">Overlap: {settings.chunkOverlap} tokens ({Math.round((settings.chunkOverlap / settings.chunkSize) * 100)}%)</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <input type="checkbox" id="titleInjection" checked={settings.titleInjection} onChange={(e) => updateVectorizationSettings('titleInjection', toBool(e.target.checked))} disabled={isSearchMode} className="checkbox" />
            <label htmlFor="titleInjection" className="label">Title Injection / Heading Context</label>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center gap-2"><i className="fas fa-flask"></i>Text Cleaning & Testing</h3>
        </div>
        <div className="card-content space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-2"><input type="checkbox" id="normalizeWhitespace" checked={settings.textCleaning.normalizeWhitespace} onChange={(e) => updateVectorizationSettings('textCleaning.normalizeWhitespace', toBool(e.target.checked))} disabled={isSearchMode} className="checkbox" /><label htmlFor="normalizeWhitespace" className="label">Normalize whitespace</label></div>
              <div className="flex items-center space-x-2"><input type="checkbox" id="removeMultipleBlankLines" checked={settings.textCleaning.removeMultipleBlankLines} onChange={(e) => updateVectorizationSettings('textCleaning.removeMultipleBlankLines', toBool(e.target.checked))} disabled={isSearchMode} className="checkbox" /><label htmlFor="removeMultipleBlankLines" className="label">Remove multiple blank lines</label></div>
              <div className="flex items-center space-x-2"><input type="checkbox" id="removeFooterLines" checked={settings.textCleaning.removeFooterLines} onChange={(e) => updateVectorizationSettings('textCleaning.removeFooterLines', toBool(e.target.checked))} disabled={isSearchMode} className="checkbox" /><label htmlFor="removeFooterLines" className="label">Remove common footer lines</label></div>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="label">Test Cleaning</label>
                <textarea value={testText} onChange={(e) => setTestText(e.target.value)} placeholder="Paste text to test cleaning rules..." rows={4} className="textarea" />
                <button onClick={testCleaning} className="btn btn-sm">Test Cleaning</button>
              </div>
              {cleanedText && <div className="space-y-2"><label className="label">Cleaned Result</label><textarea value={cleanedText} readOnly rows={4} className="textarea bg-muted" /></div>}
            </div>
          </div>
          <hr className="separator" />
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="label">Custom Removal Patterns (regex)</label>
              <div className="flex gap-2"><input value={newPattern} onChange={(e) => setNewPattern(e.target.value)} placeholder="e.g., ^\d+\s*$ for page numbers" disabled={isSearchMode} className="input" /><button type="button" onClick={addPattern} disabled={isSearchMode} className="btn">Add</button></div>
            </div>
            {settings.textCleaning.removePatterns.length > 0 && <div className="space-y-2"><label className="label">Active Patterns</label><div className="space-y-2">{settings.textCleaning.removePatterns.map((pattern, index) => (<div key={index} className="flex items-center justify-between p-2 border rounded"><code className="text-sm">{pattern}</code><button onClick={() => removePattern(index)} disabled={isSearchMode} className="btn btn-ghost btn-sm">Remove</button></div>))}</div></div>}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-4">
        <button onClick={() => setIsLoadDialogOpen(true)} disabled={isSearchMode} className="btn btn-outline">Import</button>
        <button onClick={() => setIsSaveDialogOpen(true)} disabled={isSearchMode} className="btn btn-outline">Export</button>
        <button onClick={handleSave} disabled={isSearchMode} className="btn">Save</button>
      </div>

      {isSaveDialogOpen && (
        <div className="dialog-overlay" onClick={() => setIsSaveDialogOpen(false)}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2 className="dialog-title">Export Settings</h2>
              <p className="dialog-description">Copy the JSON below to export your current settings.</p>
            </div>
            <div className="grid gap-4 py-4">
              <textarea readOnly value={JSON.stringify(ragSettings, null, 2)} rows={20} className="textarea" />
            </div>
            <div className="dialog-footer">
              <button onClick={() => { navigator.clipboard.writeText(JSON.stringify(ragSettings, null, 2)); alert("Settings copied to clipboard."); }} className="btn">Copy to Clipboard</button>
              <button onClick={() => setIsSaveDialogOpen(false)} className="btn btn-secondary">Close</button>
            </div>
          </div>
        </div>
      )}

      {isLoadDialogOpen && (
        <div className="dialog-overlay" onClick={() => setIsLoadDialogOpen(false)}>
          <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2 className="dialog-title">Import Settings</h2>
              <p className="dialog-description">Paste your JSON configuration below to load settings.</p>
            </div>
            <div className="grid gap-4 py-4">
              <textarea value={jsonToLoad} onChange={(e) => setJsonToLoad(e.target.value)} placeholder="Paste your settings JSON here..." rows={20} className="textarea" />
            </div>
            <div className="dialog-footer">
              <button onClick={handleLoadSettings} className="btn">Load Settings</button>
              <button onClick={() => setIsLoadDialogOpen(false)} className="btn btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const WeaviateSettingsCard = ({ settings, dimensions, updateSettings, disabled }) => (
  <div className="card">
    <div className="card-header">
      <h3 className="card-title flex items-center gap-2"><i className="fas fa-sliders-h"></i>Weaviate Specific Settings</h3>
    </div>
    <div className="card-content space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="space-y-2"><label htmlFor="vectorIndexType" className="label">Vector Index Type</label><select value={settings.vectorIndexType} onChange={(e) => updateSettings('weaviateSettings.vectorIndexType', e.target.value)} disabled={disabled} className="select"><option value="hnsw">HNSW (Recommended)</option><option value="flat">Flat (Exact)</option><option value="dynamic">Dynamic</option></select></div>
          <div className="space-y-2"><label htmlFor="distanceMetric" className="label">Distance Metric</label><select value={settings.distanceMetric} onChange={(e) => updateSettings('weaviateSettings.distanceMetric', e.target.value)} disabled={disabled} className="select"><option value="cosine">Cosine (Recommended)</option><option value="l2">Euclidean (L2)</option><option value="ip">Inner Product</option></select></div>
          <div className="flex items-center space-x-2"><input type="checkbox" id="useExternalEmbeddings" checked={settings.useExternalEmbeddings} onChange={(e) => updateSettings('weaviateSettings.useExternalEmbeddings', toBool(e.target.checked))} disabled={disabled} className="checkbox" /><label htmlFor="useExternalEmbeddings" className="label">Use External Embeddings</label></div>
          <div className="space-y-2"><label htmlFor="memoryProfile" className="label">Memory Profile</label><select value={settings.memoryProfile} onChange={(e) => updateSettings('weaviateSettings.memoryProfile', e.target.value)} disabled={disabled} className="select"><option value="small">Small</option><option value="medium">Medium</option><option value="large">Large</option></select></div>
        </div>
        <div className="space-y-4">
          <div className="flex items-center space-x-2"><input type="checkbox" id="enableBM25" checked={settings.enableBM25} onChange={(e) => updateSettings('weaviateSettings.enableBM25', toBool(e.target.checked))} disabled={disabled} className="checkbox" /><label htmlFor="enableBM25" className="label">Enable BM25 (Keyword Search)</label></div>
          <div className="flex items-center space-x-2"><input type="checkbox" id="multiTenancy" checked={settings.multiTenancy} onChange={(e) => updateSettings('weaviateSettings.multiTenancy', toBool(e.target.checked))} disabled={disabled} className="checkbox" /><label htmlFor="multiTenancy" className="label">Enable Multi-Tenancy</label></div>
          {settings.multiTenancy && <div className="space-y-2"><label htmlFor="tenant" className="label">Default Tenant</label><input id="tenant" value={settings.tenant} onChange={(e) => updateSettings('weaviateSettings.tenant', e.target.value)} disabled={disabled} className="input" /></div>}
        </div>
      </div>
    </div>
  </div>
);

const KdbAiSettingsCard = ({ settings, dimensions, updateSettings, addPartitionColumn, removePartitionColumn, newPartitionColumn, setNewPartitionColumn, pqError, needsNormalization, disabled }) => (
  <div className="card">
    <div className="card-header">
      <h3 className="card-title flex items-center gap-2"><i className="fas fa-sliders-h"></i>KDB.AI Specific Settings</h3>
    </div>
    <div className="card-content space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="space-y-2"><label htmlFor="indexType" className="label">Index Type</label><select value={settings.indexType} onChange={(e) => updateSettings('kdbAiSettings.indexType', e.target.value)} disabled={disabled} className="select"><option value="hnsw">HNSW</option><option value="ivf">IVF</option><option value="flat">Flat</option></select></div>
          <div className="space-y-2"><label htmlFor="metricType" className="label">Distance Metric</label><select value={settings.metricType} onChange={(e) => updateSettings('kdbAiSettings.metricType', e.target.value)} disabled={disabled} className="select"><option value="cosine">Cosine</option><option value="l2">L2</option><option value="ip">Inner Product</option></select></div>
          <div className="flex items-center space-x-2"><input type="checkbox" id="normalizeVectors" checked={settings.normalizeVectors} onChange={(e) => updateSettings('kdbAiSettings.normalizeVectors', toBool(e.target.checked))} disabled={disabled} className="checkbox" /><label htmlFor="normalizeVectors" className="label">Normalize vectors on write</label></div>
          {needsNormalization && !settings.normalizeVectors && <div className="alert"><i className="fas fa-exclamation-circle"></i><p className="alert-description">Cosine similarity works best with normalized vectors.</p></div>}
        </div>
        <div className="space-y-4">
          <div className="space-y-2"><label htmlFor="compressionType" className="label">Compression Type</label><select value={settings.compressionType} onChange={(e) => updateSettings('kdbAiSettings.compressionType', e.target.value)} disabled={disabled} className="select"><option value="none">None</option><option value="sq8">SQ8</option><option value="pq">PQ</option></select></div>
          {settings.compressionType === 'pq' && <div className="space-y-2"><label htmlFor="pqM" className="label">PQ M (Sub-vectors)</label><input id="pqM" type="number" value={settings.pqM} onChange={(e) => updateSettings('kdbAiSettings.pqM', parseInt(e.target.value))} className={`input ${pqError ? 'border-destructive' : ''}`} disabled={disabled} />{pqError && <p className="text-sm text-destructive">PQ M must divide dimensions evenly. Auto-adjusting to {getAutoAdjustedPqM(dimensions, settings.pqM)}.</p>}</div>}
          <div className="flex items-center space-x-2"><input type="checkbox" id="enableGpu" checked={settings.enableGpu} onChange={(e) => updateSettings('kdbAiSettings.enableGpu', toBool(e.target.checked))} disabled={disabled} className="checkbox" /><label htmlFor="enableGpu" className="label">Enable GPU Acceleration</label></div>
        </div>
      </div>
      <hr className="separator" />
      <div className="space-y-4">
        <label className="label">Partition Columns</label>
        <div className="flex gap-2"><input value={newPartitionColumn} onChange={(e) => setNewPartitionColumn(e.target.value)} placeholder="Add partition column" disabled={disabled} className="input" /><button type="button" onClick={addPartitionColumn} disabled={disabled} className="btn">Add</button></div>
        <div className="flex flex-wrap gap-2 mt-2">{settings.partitionColumns.map((col, i) => <span key={i} className="badge badge-secondary cursor-pointer" onClick={() => removePartitionColumn(i)}>{col}<span className="ml-1">×</span></span>)}</div>
      </div>
    </div>
  </div>
);

function RAGSearchPage({ ragSettings, setRagSettings }) {
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const searchSettings = ragSettings.search;

  const updateSearchSettings = (path, value) => {
    setRagSettings(prev => {
      const keys = path.split('.');
      const updated = JSON.parse(JSON.stringify(prev)); // Deep copy
      let current = updated.search;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const performSearch = async () => {
    if (!searchSettings.query.trim()) return;
    setIsSearching(true);
    setTimeout(() => {
      const mockResults = [
        { id: '1', content: 'Vector embeddings are dense numerical representations...', score: 0.95, effectiveScore: 0.92, metadata: { source: 'embeddings_guide.pdf', title: 'Intro to Embeddings', page: 1, section: 'Overview', timestamp: '2024-01-15T10:30:00Z' } },
        { id: '2', content: 'Retrieval-Augmented Generation (RAG) combines...', score: 0.89, effectiveScore: 0.88, metadata: { source: 'rag_fundamentals.pdf', title: 'RAG Architecture', page: 3, section: 'Core Concepts', timestamp: '2024-01-10T14:20:00Z' } },
      ];
      setSearchResults(mockResults);
      setIsSearching(false);
    }, 1000);
  };

  const isHybrid = searchSettings.retrieval.retrievalMode === 'hybrid';
  const isAlphaRelevant = searchSettings.retrieval.fusionStrategy === 'weighted-sum' || searchSettings.retrieval.fusionStrategy === 'auto-tune';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <i className="fas fa-search text-primary text-2xl"></i>
        <div>
          <h1 className="text-3xl font-bold text-foreground">RAG Search</h1>
          <p className="text-muted-foreground">Search your knowledge base with advanced retrieval and fusion</p>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3 className="card-title">Search Query</h3></div>
        <div className="card-content space-y-4">
          <div className="space-y-2">
            <label htmlFor="searchQuery" className="label">Query</label>
            <div className="flex gap-2">
              <input id="searchQuery" value={searchSettings.query} onChange={(e) => updateSearchSettings('query', e.target.value)} placeholder="What would you like to know about?" onKeyDown={(e) => e.key === 'Enter' && performSearch()} className="input" />
              <button onClick={performSearch} disabled={isSearching || !searchSettings.query.trim()} className="btn">{isSearching ? 'Searching...' : 'Search'}</button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><h3 className="card-title">Retrieval & Fusion Strategy</h3></div>
        <div className="card-content space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-2"><label htmlFor="retrievalMode" className="label">Retrieval Mode</label><select value={searchSettings.retrieval.retrievalMode} onChange={(e) => updateSearchSettings('retrieval.retrievalMode', e.target.value)} className="select"><option value="hybrid">Hybrid</option><option value="vector">Vector Only</option><option value="keyword">Keyword Only</option></select></div>
              {isHybrid && <div className="space-y-2"><label htmlFor="fusionStrategy" className="label">Fusion Strategy</label><select value={searchSettings.retrieval.fusionStrategy} onChange={(e) => updateSearchSettings('retrieval.fusionStrategy', e.target.value)} className="select"><option value="rrf">RRF</option><option value="weighted-sum">Weighted Sum</option><option value="z-score-sum">Z-score Sum</option><option value="auto-tune">Auto-tune Alpha</option></select></div>}
              {isHybrid && isAlphaRelevant && <div className="space-y-2"><label htmlFor="alpha" className="label">Alpha</label><input id="alpha" type="number" step="0.05" min="0" max="1" value={searchSettings.retrieval.alpha} onChange={(e) => updateSearchSettings('retrieval.alpha', parseFloat(e.target.value))} className="input" /></div>}
              <div className="space-y-2"><label htmlFor="similarityThreshold" className="label">Similarity Threshold</label><input id="similarityThreshold" type="number" step="0.1" min="0" max="1" value={searchSettings.retrieval.similarityThreshold} onChange={(e) => updateSearchSettings('retrieval.similarityThreshold', parseFloat(e.target.value))} className="input" /></div>
            </div>
            <div className="space-y-4">
              <div className="space-y-2"><label htmlFor="poolK" className="label">Pool K</label><input id="poolK" type="number" value={searchSettings.retrieval.poolK} onChange={(e) => updateSearchSettings('retrieval.poolK', parseInt(e.target.value))} className="input" /></div>
              <div className="space-y-2"><label htmlFor="finalChunks" className="label">Final Chunks to LLM</label><input id="finalChunks" type="number" value={searchSettings.retrieval.finalChunks} onChange={(e) => updateSearchSettings('retrieval.finalChunks', parseInt(e.target.value))} className="input" /></div>
              <div className="flex items-center space-x-2"><input type="checkbox" id="reranking" checked={searchSettings.retrieval.reranking} onChange={(e) => updateSearchSettings('retrieval.reranking', toBool(e.target.checked))} disabled={isHybrid} className="checkbox" /><label htmlFor="reranking" className="label">Enable reranking {isHybrid && '(Required)'}</label></div>
            </div>
          </div>
        </div>
      </div>

      {searchResults.length > 0 && <div className="card"><div className="card-header"><h3 className="card-title">Search Results</h3></div><div className="card-content space-y-4">{searchResults.map((result, index) => <div key={result.id} className="border rounded-lg p-4 space-y-3"><div className="flex items-start justify-between"><div className="flex items-center gap-2"><span className="badge badge-outline">Raw: {result.score.toFixed(2)}</span><span className="badge badge-secondary">Effective: {result.effectiveScore.toFixed(2)}</span></div><div className="text-sm text-muted-foreground">{result.metadata.source}</div></div><div className="text-foreground">{result.content}</div></div>)}</div></div>}
    </div>
  );
}

function SettingsModal({ isOpen, onClose, ragSettings, setRagSettings, indexId }) {
  const [activeTab, setActiveTab] = useState('settings');

  if (!isOpen) {
    return null;
  }

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content-large" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b pb-4 mb-4 flex-shrink-0">
          <h2 className="text-2xl font-semibold">RAG Configuration Settings</h2>
          <div className="flex items-center gap-4">
            <button onClick={onClose} className="btn btn-ghost btn-sm text-2xl leading-none p-1 h-auto">&times;</button>
          </div>
        </div>
        
        <div className="w-full flex-shrink-0">
          <div className="tabs-list">
            <button onClick={() => setActiveTab('settings')} className={`tabs-trigger ${activeTab === 'settings' ? 'active' : ''}`}><i className="fas fa-cogs mr-2"></i>Vectorization</button>
            <button onClick={() => setActiveTab('search')} className={`tabs-trigger ${activeTab === 'search' ? 'active' : ''}`}><i className="fas fa-search mr-2"></i>RAG Search</button>
          </div>
        </div>

        <div className="mt-6 flex-grow overflow-y-auto scrollbar-thin pr-4 -mr-4">
          {activeTab === 'settings' && <VectorizationSettingsPage ragSettings={ragSettings} setRagSettings={setRagSettings} indexId={indexId} />}
          {activeTab === 'search' && <RAGSearchPage ragSettings={ragSettings} setRagSettings={setRagSettings} />}
        </div>
      </div>
    </div>
  );
}

function useAutoScroll(deps) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, deps);
  return ref;
}

const defaultSystemPrompt =
  "You are a helpful assistant. Answer concisely. Use Markdown when helpful.";

function MessageBubble({ message }) {
  const { role, content = "", chartSpec, chartSpecs, tables = [] } = message;
  const agentEventLabel =
    role === "assistant" && message?.agentEvents?.length
      ? stringifyAgentEvent(message.agentEvents[message.agentEvents.length - 1])
      : null;
  const html = useMemo(() => ({ __html: marked.parse(content || "") }), [content]);

  useEffect(() => {
    if (!content) return;
    const blocks = document.querySelectorAll("pre > code");
    blocks.forEach((code) => {
      const pre = code.parentElement;
      if (!pre || pre.querySelector(".copy-btn")) return;

      const btn = document.createElement("button");
      btn.className = "copy-btn";
      btn.innerHTML = '<i class="fas fa-copy"></i>';
      btn.onclick = async () => {
        await navigator.clipboard.writeText(code.textContent || "");
        btn.innerHTML = '<i class="fas fa-check"></i>';
        setTimeout(() => (btn.innerHTML = '<i class="fas fa-copy"></i>'), 1200);
      };
      pre.appendChild(btn);
    });
  }, [content]);

  const charts =
    chartSpecs && chartSpecs.length
      ? chartSpecs
      : chartSpec
      ? [chartSpec]
      : [];

  return (
    <div className={`msg ${role === "user" ? "user" : "assistant"}`}>
      <div className="msg-avatar">
        <i className={`fas ${role === "user" ? "fa-user" : "fa-robot"}`}></i>
      </div>
      <div className="msg-body">
        <div className="msg-role">
          {role}
          {agentEventLabel && <span className="agent-event-label">{agentEventLabel}</span>}
        </div>
        {content && (
          <div className="msg-content markdown" dangerouslySetInnerHTML={html} />
        )}
        {charts.map((spec, idx) => (
          <div key={idx} style={chartStyles.card}>
            {spec?.title && <div style={chartStyles.title}>{spec.title}</div>}
            {spec?.description && <div style={chartStyles.desc}>{spec.description}</div>}
            <InteractiveChart spec={spec} />
          </div>
        ))}
        {tables.map((table, idx) => (
          <div key={`table-${idx}`} style={chartStyles.card}>
            {table?.title && <div style={chartStyles.title}>{table.title}</div>}
            {table?.description && <div style={chartStyles.desc}>{table.description}</div>}
            <DataTable table={table} />
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ onPick }) {
  const examples = [
    "What is artificial intelligence?",
    "Explain quantum computing in simple terms",
    "Write a Python function to sort a list"
  ];

  return (
    <div className="empty">
      <div className="empty-icon">
        <i className="fas fa-comments"></i>
      </div>
      <div className="empty-title">Start a conversation</div>
      <div className="empty-subtitle">Choose a topic below or type your own message</div>
      <div className="empty-grid">
        {examples.map((e) => (
          <button key={e} className="chip" onClick={() => onPick(e)}>
            <i className="fas fa-lightbulb"></i>
            {e}
          </button>
        ))}
      </div>
      <div className="empty-hint">
        <i className="fas fa-keyboard"></i>
        Press Shift+Enter for newline
      </div>
    </div>
  );
}

function ChatInterface() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [systemPrompt, setSystemPrompt] = useState(defaultSystemPrompt);
  const [isStreaming, setIsStreaming] = useState(false);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [uploadPanelOpen, setUploadPanelOpen] = useState(true);
  const [configPanelOpen, setConfigPanelOpen] = useState(true);
  const [controller, setController] = useState(null);
  const { theme, toggleTheme } = useTheme();

  // RAG + guardrails
  const [indexes, setIndexes] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState("");
  const [newIndexName, setNewIndexName] = useState("");
  const [useRag, setUseRag] = useState(false);
  const [guardrails, setGuardrails] = useState("standard");
  const [searchMode, setSearchMode] = useState("hybrid");

  // RAG Type Toggle (Index vs Agentic RAG)
  const [ragType, setRagType] = useState("index"); // "index" or "agentic"
  // const agenticAgents = [
  //   "hr-assistant",
  //   "sample_graph",
  //   "financial-doc-search",
  //   "legal-document-assistant",
  //   "technical-support-agent",
  //   "research-paper-analyzer"
  // ];

  // Vector Database Type Toggle
  const [vectorDbType, setVectorDbType] = useState("index"); // "index" or "workflow"
  const [dbFilterType, setDbFilterType] = useState('weaviate');

  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState("");
  
  const [agenticAgents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");

  // Advanced Settings Modal
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  // Sessions
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [creatingSession, setCreatingSession] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingSessionTitle, setEditingSessionTitle] = useState("");

  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");

  // Combined RAG Settings State
  const [ragSettings, setRagSettings] = useState(getDefaultRagSettings());

  const scrollRef = useAutoScroll([messages.length, isStreaming, input]);

  useEffect(() => {
    fetchIndexes();
  }, [dbFilterType]);

  useEffect(() => {
    fetchWorkflows();
    fetchSessions();
    fetchAgents();
  }, []);

  async function fetchIndexes() {
    try {
      const res = await fetch(`${API_BASE_URL}/indexes?vs_url=${encodeURIComponent(VS_URL)}&dbtype=${encodeURIComponent(dbFilterType)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      const list = Array.isArray(raw) ? raw.map(item => (typeof item === "string" ? { id: item, name: item } : item)).filter(Boolean) : [];
      setIndexes(list);
      setSelectedIndex(curId => list.some(x => x.id === curId) ? curId : (list[0]?.id ?? ""));
    } catch (err) {
      console.error("Failed to fetch indexes:", err);
      setIndexes([]);
      setSelectedIndex("");
    }
  }

  async function fetchWorkflows() {
    try {
      const response = await fetch(`${API_BASE_URL}/workflows`);
      const data = await response.json();
      if (data && Array.isArray(data)) {
        setWorkflows(data);
        setSelectedWorkflow((cur) => {
          const currentWorkflow = data.find(w => w.dagid === cur);
          return currentWorkflow ? cur : (data[0]?.dagid || "");
        });
      } else {
        setWorkflows([]);
        setSelectedWorkflow("");
      }
    } catch (err) {
      console.error("Failed to fetch workflows:", err);
      setWorkflows([]);
      setSelectedWorkflow("");
    }
  }

    async function fetchAgents() {
    try {
      const response = await fetch(`${API_BASE_URL}/agents`);
      const data = await response.json();
      console.log("Fetched agents:", data);
      if (data && Array.isArray(data)) {
        setAgents(data);
        setSelectedAgent((cur) => {
          const currentAgent = data.find(w => w.id === cur);
          return currentAgent ? cur : (data[0]?.id || "");
        });
      } else {
        setAgents([]);
        setSelectedAgent("");
      }
    } catch (err) {
      console.error("Failed to fetch agents:", err);
      setAgents([]);
      setSelectedAgent("");
    }
  }

  async function fetchSessions(activeSessionIdOverride) {
    const activeId =
      activeSessionIdOverride !== undefined ? activeSessionIdOverride : sessionId;
    try {
      const response = await fetch(`${API_BASE_URL}/sessions`);
      const data = await response.json();
      if (data && Array.isArray(data)) {
        setSessions(data);
        if (!activeId && data.length > 0) {
          const mostRecent = [...data].sort((a, b) => b.updated_at - a.updated_at)[0];
          await selectSession(mostRecent.id);
        } else if (!activeId && data.length === 0) {
          setSessionId(null);
          setMessages([]);
        }
      } else {
        setSessions([]);
        if (!activeId) {
          setSessionId(null);
          setMessages([]);
        }
      }
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
      setSessions([]);
      if (!activeId) {
        setSessionId(null);
        setMessages([]);
      }
    }
  }

  async function newSession() {
    setCreatingSession(true);
    try {
      const response = await fetch(`${API_BASE_URL}/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({})
      });
      if (response.ok) {
        const data = await response.json();
        setSessionId(data.id);
        setMessages([
          normalizeMessage({
            id: uuid(),
            role: "assistant",
            content: "Hello! I'm your CafeBot powered AI assistant. How can I help you today?"
          })
        ]);
        await fetchSessions();
        return data.id;
      } else {
        console.error("Failed to create session");
      }
    } catch (err) {
      console.error("Failed to create session:", err);
    } finally {
      setCreatingSession(false);
    }
  }

  async function selectSession(id) {
    cancelRenamingSession();
    try {
      const response = await fetch(`${API_BASE_URL}/sessions/${id}`);
      if (response.ok) {
        const data = await response.json();
        setSessionId(data.session.id);
        const mapped = data.messages.map((m) =>
          normalizeMessage({ id: m.id, role: m.role, content: m.content })
        );
        setMessages(
          mapped.length > 0
            ? mapped
            : [
                normalizeMessage({
                  id: uuid(),
                  role: "assistant",
                  content:
                    "Hello! I'm your CafeBot powered AI assistant. How can I help you today?"
                })
              ]
        );
      } else {
        console.error("Failed to load session");
      }
    } catch (err) {
      console.error("Failed to select session:", err);
    }
  }

  async function deleteSession(id) {
    try {
      const deletingCurrent = id === sessionId;
      if (editingSessionId === id) cancelRenamingSession();
      if (deletingCurrent) {
        setSessionId(null);
        setMessages([]);
      }
      await fetch(`${API_BASE_URL}/sessions/${id}`, { method: "DELETE" });
      await fetchSessions(deletingCurrent ? null : undefined);
    } catch (err) {
      console.error("Failed to delete session:", err);
    }
  }

  const startRenamingSession = (session) => {
    setEditingSessionId(session.id);
    setEditingSessionTitle(session.title || "");
  };

  const cancelRenamingSession = () => {
    setEditingSessionId(null);
    setEditingSessionTitle("");
  };

  async function submitSessionRename() {
    if (!editingSessionId) {
      console.log("No session is being edited");
      return
    };
    try {
      const params = new URLSearchParams();
      params.set("title", editingSessionTitle ?? "");
      const response = await fetch(
        `${API_BASE_URL}/sessions/${editingSessionId}/rename?${params.toString()}`
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const updated = await response.json();
      setSessions((prev) =>
        prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s))
      );
      cancelRenamingSession();
    } catch (err) {
      console.error("Failed to rename session:", err);
      alert("Failed to rename session");
    }
  }

  async function createIndex() {
    const name = newIndexName.trim();
    if (!name) return;
    if (!/^[A-Z]/.test(name)) {
      alert("Index name must start with a capital letter");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("vs_url", VS_URL);
      formData.append("name", name);
      const response = await fetch(`${API_BASE_URL}/indexes`, { method: "POST", body: formData });
      if (response.ok) {
        setNewIndexName("");
        await fetchIndexes();
        setSelectedIndex(name);
      } else {
        const errorText = await response.text();
        console.error("Failed to create index:", errorText);
        alert("Failed to create index: " + errorText);
      }
    } catch (err) {
      console.error("Failed to create index:", err);
      alert("Failed to create index: " + err.message);
    }
  }

  async function handleUpload() {
    if (vectorDbType === "index" && !selectedIndex) {
      alert("Select a vector index first.");
      return;
    }
    if (vectorDbType === "workflow" && !selectedWorkflow) {
      alert("Select a workflow first.");
      return;
    }
    if (!files.length) {
      alert("Choose files to upload.");
      return;
    }
    setIsUploading(true);
    setUploadMsg("Uploading…");
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("vs_url", VS_URL);
        if (vectorDbType === "index") {
          formData.append("index", selectedIndex);
          formData.append("dbFilterType", dbFilterType);
        } else {
          formData.append("dagid", selectedWorkflow);
        }
        const response = await fetch(`${API_BASE_URL}/upload`, { method: "POST", body: formData });
        if (!response.ok) throw new Error(`Upload failed for ${file.name}`);
      }
      setUploadMsg("✅ Upload complete. Docs are being indexed.");
      setFiles([]);
    } catch (err) {
      console.error("Upload failed:", err);
      setUploadMsg(`❌ Upload failed: ${err.message}`);
    } finally {
      setIsUploading(false);
      setTimeout(() => setUploadMsg(""), 3000);
    }
  }

  async function sendMessage(userText) {
    if (!userText?.trim() || isStreaming) return;
    let currentSessionId = sessionId;
    if (!currentSessionId) {
      const sid = await newSession();
      currentSessionId = sid || sessionId || "";
    }
    const userMsg = normalizeMessage({
      id: uuid(),
      role: "user",
      content: userText.trim()
    });
    const draftAssistant = normalizeMessage({
      id: uuid(),
      role: "assistant",
      content: ""
    });
    setMessages((m) => [...m, userMsg, draftAssistant]);
    setInput("");
    const aborter = new AbortController();
    setController(aborter);
    setIsStreaming(true);
    try {
      const prior = messages.filter((m) => typeof m?.content === "string").map((m) => ({ role: m.role, content: String(m.content) }));
      const conversationHistory = [...prior, { role: "user", content: userMsg.content }];
      const payload = {
        system: String(systemPrompt ?? ""),
        messages: conversationHistory,
        use_rag: !!useRag,
        session_id: currentSessionId,   // <-- add this
        guardrails
      };
                //{useRag && ragType === "agentic" && (
      console.log("Sending chat payload:", payload);
      if (useRag) {
        if (VS_URL) payload.vs_url = String(VS_URL1);
        if (ragType === "index") {
          if (vectorDbType === "index" && selectedIndex != null) {
            payload.index = String(selectedIndex);
            payload.dbFilterType = dbFilterType;
          } 
          else if (vectorDbType === "workflow" && selectedWorkflow) {
            const wf = workflows.find((w) => w.dagid === selectedWorkflow);
            if (wf?.index != null) payload.index = String(wf.index);
          }
        }
        else if (ragType === "agentic" && selectedAgent!= null) {
          payload.agent = String(selectedAgent);
        }
      }
      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: aborter.signal
      });
      if (!response.ok) {
        let errText = `HTTP ${response.status}`;
        console.error("Chat error response:", response);
        try {
          const maybeJson = await response.json();
          if (maybeJson?.detail) errText += ` – ${JSON.stringify(maybeJson.detail)}`;
        } catch {}
        throw new Error(errText);
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let assistantText = "";
      let jsonBuffer = "";

      const appendText = (text) => {
        if (!text) return;
        assistantText += text;
        jsonBuffer = "";
        setMessages((prev) =>
          prev.map((m) =>
            m.id === draftAssistant.id
              ? { ...m, content: assistantText }
              : m
          )
        );
      };

      const applyCharts = (specs) => {
        if (!specs || !specs.length) return;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === draftAssistant.id
              ? {
                  ...m,
                  chartSpec: specs.length === 1 ? specs[0] : null,
                  chartSpecs: specs.length > 1 ? specs : null,
                  content: assistantText
                }
              : m
          )
        );
      };

      const applyTables = (tables) => {
        if (!tables || !tables.length) return;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === draftAssistant.id
              ? {
                  ...m,
                  tables: [...(m.tables || []), ...tables],
                  content: assistantText
                }
              : m
          )
        );
      };

      const applyAgentEvent = (event) => {
        if (!event) return;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === draftAssistant.id
              ? {
                  ...m,
                  agentEvents: [...(m.agentEvents || []), event],
                  content: assistantText
                }
              : m
          )
        );
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const ssePayloads = parseSSEPayloads(chunk);
        //console.log("Received chat payload:", ssePayloads);
        let handled = false;

        if (ssePayloads && ssePayloads.length) {
          handled = true;
          for (const payload of ssePayloads) {
            const parsed = safeJSON(payload);
            if (parsed && typeof parsed === "object") {
              if (isAgentEventPayload(parsed)) {
                applyAgentEvent(parsed);
                continue;
              }
              const specs = mapMultiChartPayloadToSpecs(parsed);
              if (specs && specs.length) {
                applyCharts(specs);
                continue;
              }
              const table = mapPayloadToTableSpec(parsed);
              if (table) {
                applyTables([table]);
                continue;
              }
              const text = extractTextFromPayload(parsed);
              if (text) appendText(text);
            } else if (payload) {
              appendText(payload + "\n");
            }
          }
        }

        if (handled) continue;

        jsonBuffer += chunk;
        const maybeObject = safeJSON(jsonBuffer.trim());
        if (maybeObject && typeof maybeObject === "object") {
          if (isAgentEventPayload(maybeObject)) {
            applyAgentEvent(maybeObject);
            jsonBuffer = "";
            continue;
          }
          const specs = mapMultiChartPayloadToSpecs(maybeObject);
          if (specs && specs.length) {
            applyCharts(specs);
            jsonBuffer = "";
            continue;
          }
          const table = mapPayloadToTableSpec(maybeObject);
          if (table) {
            applyTables([table]);
            jsonBuffer = "";
            continue;
          }
          const text = extractTextFromPayload(maybeObject);
          if (text) {
            appendText(text);
            jsonBuffer = "";
            continue;
          }
        } else if (jsonBuffer.trim().startsWith("{") || jsonBuffer.trim().startsWith("[")) {
          // wait for more data
          continue;
        }

        appendText(chunk);
      }
      await fetchSessions();
    } catch (err) {
      console.error("Chat failed:", err);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === draftAssistant.id
            ? {
                ...m,
                content: `${m.content || ""}\n\n> ❌ ${err.message || "Chat failed"}`,
                chartSpec: null,
                chartSpecs: null
              }
            : m
        )
      );
    } finally {
      setIsStreaming(false);
      setController(null);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    sendMessage(input);
  }

  function stopStream() {
    if (controller) {
      controller.abort();
      setIsStreaming(false);
      setController(null);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files?.length) {
      setFiles(Array.from(e.dataTransfer.files));
      e.dataTransfer.clearData();
    }
  }

  async function openSettingsModal() {
    console.log("Opening settings modal" + (newIndexName ? ` for new index "${newIndexName}"` : ` for selected index "${selectedIndex}"`));  
    const indexToLoad = newIndexName || selectedIndex;

    if (!indexToLoad) {
      setRagSettings(getDefaultRagSettings());
      setSettingsModalOpen(true);
      return;
    }
    //@Aj - New block to supprt add mode 
    if (newIndexName) {
      setRagSettings(getDefaultRagSettings());
      setSettingsModalOpen(true);
      backend.settings.mode = "add";
      backend.settings.newName = newIndexName;
      return;
    }
    //@Aj - New line to supprt edit mode 
    backend.settings.mode = "edit";
    try {
      const loaded = await backend.settings.load({ indexId: indexToLoad });
      
      let settings;
      if (Array.isArray(loaded) && loaded[0] && typeof loaded[0].create_params === 'string') {
        try {
          settings = JSON.parse(loaded[0].create_params);
        } catch (e) {
          console.error("Failed to parse create_params JSON:", e);
          settings = null;
        }
      } else if (loaded && loaded.vectorization && loaded.search) {
        settings = loaded;
      }

      if (settings && settings.vectorization && settings.search) {
        setRagSettings(settings);
      } else {
        console.warn("Loaded settings are incomplete or missing, falling back to defaults.", loaded);
        setRagSettings(getDefaultRagSettings());
      }
    } catch (error) {
      console.error("Failed to load settings:", error);
      setRagSettings(getDefaultRagSettings());
    }
    setSettingsModalOpen(true);
  }

  function getWorkflowDisplayName(dagid) {
    const workflow = workflows.find(w => w.dagid === dagid);
    return workflow ? `${workflow.dagid} (${workflow.index})` : dagid;
  }

  function getSelectedWorkflowIndex() {
    const workflow = workflows.find(w => w.dagid === selectedWorkflow);
    return workflow ? workflow.index : selectedWorkflow;
  }

  function formatSessionTime(timestamp) {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays <= 1) return "Today";
    if (diffDays === 2) return "Yesterday";
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  }

  function getRagDisplayInfo() {
    if (!useRag) return "";
    if (ragType === "index") {
      if (vectorDbType === "index") {
        const indexName = indexes.find(i => i.id === selectedIndex)?.name || selectedIndex;
        return indexName ? `${indexName} (${searchMode.toUpperCase()})` : "";
      } else {
        return selectedWorkflow ? `${getSelectedWorkflowIndex()} (${searchMode.toUpperCase()})` : "";
      }
    } else {
      return selectedAgent ? `${selectedAgent} (AGENTIC)` : "";
    }
  }

  return (
    <div className="chat-shell">
      <aside className={`sidebar history-sidebar ${historyPanelOpen ? 'open' : ''}`}>
        <div className="sidebar-toggle" onClick={() => setHistoryPanelOpen(!historyPanelOpen)}>
          <div className="sidebar-toggle-text">History</div>
          <i className={`fas ${historyPanelOpen ? 'fa-chevron-left' : 'fa-chevron-right'}`}></i>
        </div>
        {historyPanelOpen && (
          <div className="sidebar-content">
            <div className="sidebar-header"><div className="sidebar-title"><i className="fas fa-history"></i>Chat History</div></div>
            <div className="sidebar-body">
              <div className="sidebar-section"><button className="primary w-full new-chat-btn" onClick={newSession} disabled={creatingSession}><i className="fas fa-plus"></i>{creatingSession ? "Creating…" : "New Chat"}</button></div>
              <div className="sidebar-section">
                <div className="section-heading"><i className="fas fa-clock"></i>Recent Conversations</div>
                <div className="sessions-list">
                  {sessions.map((s) => {
                    const isEditing = editingSessionId === s.id;
                    return (
                      <div
                        key={s.id}
                        role="button"
                        tabIndex={0}
                        className={`session-item ${sessionId === s.id ? "active" : ""}`}
                        onClick={() => selectSession(s.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            selectSession(s.id);
                          }
                        }}
                        title={s.title}
                      >
                        <div className="session-icon"><i className="fas fa-comment-dots"></i></div>
                        <div className="session-info">
                          {isEditing ? (
                            <>
                              <input
                                className="session-edit-input"
                                value={editingSessionTitle}
                                onChange={(e) => setEditingSessionTitle(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    submitSessionRename();
                                  } else if (e.key === "Escape") {
                                    e.stopPropagation();
                                    cancelRenamingSession();
                                  } else if (e.key === " " || e.key === "Spacebar") {
                                    e.stopPropagation();
                                  }
                                }}
                                autoFocus
                              />
                              <div className="session-time">{formatSessionTime(s.updated_at || s.created_at)}</div>
                            </>
                          ) : (
                            <>
                              <div className="session-title">{s.title || "Untitled Chat"}</div>
                              <div className="session-time">{formatSessionTime(s.updated_at || s.created_at)}</div>
                            </>
                          )}
                        </div>
                        <div className="session-actions" style={isEditing ? { opacity: 1 } : undefined}>
                          {isEditing ? (
                            <>
                              <button
                                className="session-action-btn"
                                title="Save name"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  submitSessionRename();
                                }}
                              >
                                <i className="fas fa-check"></i>
                              </button>
                              <button
                                className="session-action-btn"
                                title="Cancel rename"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  cancelRenamingSession();
                                }}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </>
                          ) : (
                            <button
                              className="session-action-btn"
                              title="Rename chat"
                              onClick={(e) => {
                                e.stopPropagation();
                                startRenamingSession(s);
                              }}
                            >
                              <i className="fas fa-pencil-alt"></i>
                            </button>
                          )}
                          <button
                            className="session-action-btn"
                            title="Delete chat"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSession(s.id);
                            }}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </aside>

      <aside className={`sidebar upload-sidebar ${uploadPanelOpen ? 'open' : ''}`}>
        <div className="sidebar-toggle" onClick={() => setUploadPanelOpen(!uploadPanelOpen)}>
          <div className="sidebar-toggle-text">Upload</div>
          <i className={`fas ${uploadPanelOpen ? 'fa-chevron-left' : 'fa-chevron-right'}`}></i>
        </div>
        {uploadPanelOpen && (
          <div className="sidebar-content">
            <div className="sidebar-header"><div className="sidebar-title"><i className="fas fa-layer-group"></i>Index Management</div></div>
            <div className="sidebar-body">
              <div className="sidebar-section">
                <div className="section-heading"><i className="fas fa-database"></i>Vector Database</div>
                <div className="vector-db-toggle">
                  <label className="toggle-row">
                    <div className="toggle-info"><span className="toggle-label"><i className="fas fa-database"></i>Weaviate</span></div>
                    <div className="toggle-switch"><input type="checkbox" checked={dbFilterType === "kdb-ai"} onChange={(e) => setDbFilterType(e.target.checked ? "kdb-ai" : "weaviate")} /><span className="slider"></span></div>
                    <div className="toggle-info"><span className="toggle-label"><i className="fas fa-database"></i>KDB-X</span></div>
                  </label>
                </div>
                <div className="vector-db-toggle">
                  <label className="toggle-row">
                    <div className="toggle-info"><span className="toggle-label"><i className="fas fa-layer-group"></i>Index</span></div>
                    <div className="toggle-switch"><input type="checkbox" checked={vectorDbType === "workflow"} onChange={(e) => setVectorDbType(e.target.checked ? "workflow" : "index")} /><span className="slider"></span></div>
                    <div className="toggle-info"><span className="toggle-label"><i className="fas fa-project-diagram"></i>Workflow</span></div>
                  </label>
                </div>
                {vectorDbType === "index" ? (
                  <>
                    <div className="index-row">
                      <select className="select" value={selectedIndex} onChange={(e) => setSelectedIndex(e.target.value)}>
                        <option value="">Select index...</option>
                        {indexes.map((ix) => (<option key={ix.id} value={ix.id}>{ix.name}</option>))}
                      </select>
                      <button className="ghost" onClick={fetchIndexes} title="Refresh"><i className="fas fa-sync-alt"></i></button>
                      {selectedIndex && (<button className="ghost" onClick={openSettingsModal} title="Settings"><i className="fas fa-cog"></i></button>)}
                    </div>
                    <div className="index-create">
                      <input className="input" placeholder="New index name..." value={newIndexName} onChange={(e) => setNewIndexName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createIndex()} />
                      <button className="primary" onClick={openSettingsModal} disabled={!newIndexName.trim()}><i className="fas fa-plus"></i></button>
                    </div>
                  </>
                ) : (
                  <div className="workflow-selection">
                    <div className="index-row">
                      <select className="select" value={selectedWorkflow} onChange={(e) => setSelectedWorkflow(e.target.value)}>
                        <option value="">Select workflow...</option>
                        {workflows.map((workflow) => (<option key={workflow.dagid} value={workflow.dagid}>{getWorkflowDisplayName(workflow.dagid)}</option>))}
                      </select>
                      <button className="ghost" onClick={fetchWorkflows} title="Refresh"><i className="fas fa-sync-alt"></i></button>
                    </div>
                  </div>
                )}
              </div>
              <div className="sidebar-section">
                <div className="section-heading"><i className="fas fa-file"></i>File Selection</div>
                <div className="dropzone" onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }} onDrop={onDrop}>
                  <div className="dz-icon"><i className="fas fa-cloud-upload-alt"></i></div>
                  <div className="dz-title">Drag & drop files</div>
                  <div className="dz-sub">PDF, DOCX, TXT, MD, CSV…</div>
                  <input id="filePicker" type="file" multiple style={{ display: "none" }} onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                  <button className="ghost" onClick={() => document.getElementById("filePicker")?.click()}><i className="fas fa-folder-open"></i>Choose files</button>
                </div>
                {!!files.length && (
                  <div className="files-list">
                    {files.map((f) => (
                      <div key={f.name} className="file-chip">
                        <i className="fas fa-file"></i><span>{f.name}</span>
                        <button className="file-remove-btn" onClick={() => setFiles(files.filter(file => file !== f))} title="Remove file"><i className="fas fa-times"></i></button>
                      </div>
                    ))}
                  </div>
                )}
                <button className="primary w-full" onClick={handleUpload} disabled={!files.length || (vectorDbType === "index" ? !selectedIndex : !selectedWorkflow) || isUploading}><i className={`fas ${isUploading ? 'fa-spinner fa-spin' : 'fa-upload'}`}></i>{isUploading ? "Uploading…" : `Upload to ${vectorDbType === "index" ? "Index" : "Workflow"}`}</button>
                {uploadMsg && <div className="upload-msg">{uploadMsg}</div>}
              </div>
            </div>
          </div>
        )}
      </aside>

      <aside className={`sidebar config-sidebar ${configPanelOpen ? 'open' : ''}`}>
        <div className="sidebar-toggle" onClick={() => setConfigPanelOpen(!configPanelOpen)}>
          <div className="sidebar-toggle-text">Config</div>
          <i className={`fas ${configPanelOpen ? 'fa-chevron-left' : 'fa-chevron-right'}`}></i>
        </div>
        {configPanelOpen && (
          <div className="sidebar-content">
            <div className="sidebar-header"><div className="sidebar-title"><i className="fas fa-cogs"></i>Chat Configuration</div></div>
            <div className="sidebar-body">
              <div className="sidebar-section">
                <div className="section-heading"><i className="fas fa-search"></i>RAG Settings</div>
                <label className="toggle-row">
                  <div className="toggle-info"><span className="toggle-label"><i className="fas fa-search"></i>Enable RAG/Agentic</span><span className="toggle-desc">Retrieve from knowledge base</span></div>
                  <div className="toggle-switch"><input type="checkbox" checked={useRag} onChange={(e) => setUseRag(e.target.checked)} /><span className="slider"></span></div>
                </label>
                <div className="rag-type-toggle">
                  <label className="toggle-row">
                    <div className="toggle-info"><span className="toggle-label"><i className="fas fa-database"></i>RAG Index</span></div>
                    <div className="toggle-switch"><input type="checkbox" checked={ragType === "agentic"} onChange={(e) => setRagType(e.target.checked ? "agentic" : "index")} disabled={!useRag} /><span className="slider"></span></div>
                    <div className="toggle-info"><span className="toggle-label"><i className="fas fa-robot"></i>Agentic AI</span></div>
                  </label>
                </div>
                {useRag && ragType === "index" && (
                  <div className="rag-index-selection">
                    <select className="select" value={selectedIndex} onChange={(e) => setSelectedIndex(e.target.value)}>
                      <option value="">Select index...</option>
                      {indexes.map((ix) => (<option key={ix.id} value={ix.id}>{ix.name}</option>))}
                    </select>
                  </div>
                )}
                {useRag && ragType === "agentic" && (
                  <div className="agentic-agent-selection">
                    <select className="select" value={selectedAgent} onChange={(e) => setSelectedAgent(e.target.value)}>
                      <option value="">Select agent...</option>
                      {agenticAgents.map((agent) => (<option key={agent.id} value={agent.graph_id}>{agent.graph_id}</option>))}
                    </select>
                  </div>
                )}
                <div className="search-mode-section">
                  <div className="section-heading"><i className="fas fa-cog"></i>Search Algorithm</div>
                  <select className="select" value={searchMode} onChange={(e) => setSearchMode(e.target.value)} disabled={!useRag || ragType === "agentic"}>
                    <option value="hybrid">Hybrid Search</option>
                    <option value="semantic">Semantic Search</option>
                    <option value="keyword">Keyword Search</option>
                  </select>
                  <div className="algo-description">
                    {searchMode === "hybrid" && ragType === "index" && <div className="algo-desc"><i className="fas fa-info-circle"></i>Combines semantic and keyword search for best results</div>}
                    {searchMode === "semantic" && ragType === "index" && <div className="algo-desc"><i className="fas fa-info-circle"></i>Uses vector embeddings to find semantically similar content</div>}
                    {searchMode === "keyword" && ragType === "index" && <div className="algo-desc"><i className="fas fa-info-circle"></i>Traditional keyword-based search with exact matches</div>}
                    {ragType === "agentic" && <div className="algo-desc"><i className="fas fa-robot"></i>Intelligent agent handles search strategy automatically</div>}
                  </div>
                </div>
              </div>
              <div className="sidebar-section">
                <div className="section-heading"><i className="fas fa-shield-alt"></i>Safety & Guardrails</div>
                <div className="guardrails-options">
                  <label className="radio-option"><input type="radio" name="guardrails" value="off" checked={guardrails === "off"} onChange={(e) => setGuardrails(e.target.value)} /><span className="radio-label"><i className="fas fa-unlock"></i><div><div className="option-title">Off</div><div className="option-desc">No content filtering</div></div></span></label>
                  <label className="radio-option"><input type="radio" name="guardrails" value="standard" checked={guardrails === "standard"} onChange={(e) => setGuardrails(e.target.value)} /><span className="radio-label"><i className="fas fa-shield-alt"></i><div><div className="option-title">Standard</div><div className="option-desc">Basic safety filters</div></div></span></label>
                  <label className="radio-option"><input type="radio" name="guardrails" value="strict" checked={guardrails === "strict"} onChange={(e) => setGuardrails(e.target.value)} /><span className="radio-label"><i className="fas fa-lock"></i><div><div className="option-title">Strict</div><div className="option-desc">Enhanced safety measures</div></div></span></label>
                </div>
              </div>
              <div className="sidebar-section">
                <div className="section-heading"><i className="fas fa-terminal"></i>System Prompt</div>
                <textarea className="system-input" rows={4} value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} placeholder="Enter system prompt..." />
                <button className="ghost w-full" onClick={() => setSystemPrompt(defaultSystemPrompt)} title="Reset to default"><i className="fas fa-undo"></i>Reset to Default</button>
              </div>
            </div>
          </div>
        )}
      </aside>

      <main className="chat-main">
        <header className="chat-header">
          <div className="header-left"><div className="title"><i className="fas fa-brain"></i>CafeBot Powered AI Assistant{sessionId && <span className="session-id">· {sessionId.slice(0, 6)}</span>}{useRag && getRagDisplayInfo() && <span className="rag-indicator">· <i className="fas fa-database"></i>{getRagDisplayInfo()}</span>}</div></div>
          <div className="header-right">             
            <div className="theme-toggle"><button className="theme-toggle-btn" onClick={toggleTheme} title={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}><i className={`fas ${theme === 'light' ? 'fa-moon' : 'fa-sun'}`}></i></button></div>
            {isStreaming && <button className="danger" onClick={stopStream}><i className="fas fa-stop"></i>Stop</button>}
          </div>
        </header>
        <section className="chat-log" ref={scrollRef}>
          {messages.length === 0 ? (
            <EmptyState onPick={(t) => setInput(t)} />
          ) : (
            messages.map((m) => <MessageBubble key={m.id} message={m} />)
          )}
        </section>
        <form className="chat-inputbar" onSubmit={handleSubmit}>
          <div className="input-container">
            <textarea className="chat-input" placeholder="Type your message here..." value={input} onChange={(e) => setInput(e.target.value)} rows={1} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }} />
            <button className="send-btn" type="submit" disabled={!input.trim() || isStreaming} title="Send message"><i className={`fas ${isStreaming ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i></button>
          </div>
        </form>
      </main>

      <SettingsModal isOpen={settingsModalOpen} onClose={() => setSettingsModalOpen(false)} ragSettings={ragSettings} setRagSettings={setRagSettings} indexId={newIndexName || selectedIndex} />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <ChatInterface />
    </ThemeProvider>
  );
}
